import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 职业技能与实操技术 - 横屏「操作面板」风格
 *
 * 视觉特点：左右分栏（演示区+标注区）、现代仪表盘 UI、圆环进度、技能等级条
 * 适合：工具教程、操作流程、技能培训等强调"怎么做"的内容
 * 尺寸：1920x1080 横屏
 */

interface DashboardStep {
  title: string;
  detail: string;
  icon: string;
  duration: string;
}

interface SkillMetric {
  label: string;
  value: number;
  maxValue: number;
  color: string;
}

interface SkillsDashboardProps {
  title: string;
  subtitle: string;
  steps: DashboardStep[];
  metrics: SkillMetric[];
  keyOutput: string;
  tags: string[];
}

const DEFAULT_PROPS: SkillsDashboardProps = {
  title: 'Excel 数据透视表',
  subtitle: '10 分钟掌握核心操作',
  steps: [
    { title: '选择数据源', detail: '确保数据有表头，无空行空列', icon: '📋', duration: '1 min' },
    { title: '插入透视表', detail: '插入 → 数据透视表 → 选择位置', icon: '📊', duration: '2 min' },
    { title: '拖拽字段', detail: '将维度拖到行/列，指标拖到值区域', icon: '🔀', duration: '3 min' },
    { title: '设置汇总方式', detail: '右键值字段 → 值字段设置 → 求和/计数/平均', icon: '⚙️', duration: '2 min' },
    { title: '添加筛选器', detail: '将筛选维度拖到筛选区或使用切片器', icon: '🔍', duration: '2 min' },
  ],
  metrics: [
    { label: '效率提升', value: 85, maxValue: 100, color: '#10b981' },
    { label: '数据洞察', value: 90, maxValue: 100, color: '#6366f1' },
    { label: '上手难度', value: 30, maxValue: 100, color: '#f59e0b' },
  ],
  keyOutput: '从原始数据到可视化报表，5 步搞定',
  tags: ['Excel', '数据分析', '办公技能', '效率工具'],
};

const CircleProgress: React.FC<{
  value: number;
  maxValue: number;
  color: string;
  size: number;
  label: string;
  frame: number;
  startFrame: number;
}> = ({ value, maxValue, color, size, label, frame, startFrame }) => {
  const progress = interpolate(frame, [startFrame, startFrame + 40], [0, value / maxValue], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </svg>
      <div
        style={{
          position: 'relative',
          marginTop: -size / 2 - 25,
          marginLeft: 4,
          fontSize: 28,
          fontWeight: 800,
          color,
        }}
      >
        {Math.round(progress * maxValue)}%
      </div>
      <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.5)', marginTop: size / 2 - 10 }}>{label}</div>
    </div>
  );
};

export const SkillsDashboardTemplate: React.FC<Partial<SkillsDashboardProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Header
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 100 } });

  // Active step tracking
  const stepStart = 50;
  const stepInterval = 60;
  const activeFloat = interpolate(frame, [stepStart, stepStart + p.steps.length * stepInterval], [0, p.steps.length - 0.01], { extrapolateRight: 'clamp' });
  const activeStep = Math.floor(activeFloat);

  // Metrics
  const metricsStart = stepStart + p.steps.length * stepInterval + 30;

  // Output
  const outputStart = metricsStart + 80;
  const outputOpacity = interpolate(frame, [outputStart, outputStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#0f1117',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Subtle radial glow */}
      <div
        style={{
          position: 'absolute',
          top: -200,
          left: '30%',
          width: 800,
          height: 800,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.08), transparent 70%)',
        }}
      />

      {/* Header bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 100,
          background: 'rgba(255,255,255,0.03)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 40px',
          justifyContent: 'space-between',
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: '#10b981',
              boxShadow: '0 0 8px #10b981',
            }}
          />
          <span style={{ fontSize: 32, fontWeight: 700 }}>{p.title}</span>
          <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)' }}>|</span>
          <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)' }}>{p.subtitle}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {p.tags.map((tag, i) => (
            <span
              key={i}
              style={{
                padding: '4px 14px',
                background: 'rgba(99,102,241,0.12)',
                border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: 6,
                fontSize: 22,
                color: '#a5b4fc',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* LEFT PANEL - Steps */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: 40,
          width: '58%',
          bottom: 40,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 16, letterSpacing: '0.1em', fontWeight: 600 }}>
          操作步骤 STEPS
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
          {p.steps.map((step, i) => {
            const start = stepStart + i * stepInterval;
            const cardProgress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 16, stiffness: 130, mass: 0.5 },
            });
            const isActive = i === activeStep;
            const isPast = i < activeStep;

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '16px 20px',
                  background: isActive ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.02)',
                  borderRadius: 14,
                  border: isActive ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.04)',
                  opacity: cardProgress,
                  transform: `translateX(${(1 - cardProgress) * 40}px)`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* active indicator */}
                {isActive && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      background: '#6366f1',
                      borderRadius: '4px 0 0 4px',
                    }}
                  />
                )}

                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: isPast ? 'rgba(16,185,129,0.15)' : isActive ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.04)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: isPast ? 24 : 28,
                    flexShrink: 0,
                    color: isPast ? '#10b981' : 'inherit',
                  }}
                >
                  {isPast ? '✓' : step.icon}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 24, fontWeight: 600, color: isActive ? '#a5b4fc' : isPast ? 'rgba(255,255,255,0.5)' : 'white' }}>
                      {step.title}
                    </span>
                    <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }}>{step.duration}</span>
                  </div>
                  <div style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', marginTop: 4, lineHeight: 1.4 }}>
                    {step.detail}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Overall progress bar */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)' }}>完成进度</span>
            <span style={{ fontSize: 26, color: '#6366f1' }}>
              {Math.min(activeStep + 1, p.steps.length)}/{p.steps.length}
            </span>
          </div>
          <div style={{ height: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${((activeStep + 1) / p.steps.length) * 100}%`,
                background: 'linear-gradient(90deg, #6366f1, #a78bfa)',
                borderRadius: 3,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      </div>

      {/* RIGHT PANEL - Metrics + Output */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          right: 40,
          width: '36%',
          bottom: 40,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {/* Metrics section */}
        <div>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 20, letterSpacing: '0.1em', fontWeight: 600 }}>
            技能指标 METRICS
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              padding: '24px 16px',
              background: 'rgba(255,255,255,0.02)',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.04)',
            }}
          >
            {p.metrics.map((metric, i) => (
              <CircleProgress
                key={i}
                value={metric.value}
                maxValue={metric.maxValue}
                color={metric.color}
                size={100}
                label={metric.label}
                frame={frame}
                startFrame={metricsStart + i * 15}
              />
            ))}
          </div>
        </div>

        {/* Key output */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            opacity: outputOpacity,
          }}
        >
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.3)', marginBottom: 16, letterSpacing: '0.1em', fontWeight: 600, marginTop: -7 }}>
            学习成果 OUTPUT
          </div>
          <div
            style={{
              padding: '28px 24px',
              background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(99,102,241,0.05))',
              borderRadius: 16,
              border: '1px solid rgba(16,185,129,0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 26 }}>🎯</span>
              <span style={{ fontSize: 26, fontWeight: 700, color: '#10b981' }}>核心产出</span>
            </div>
            <div style={{ fontSize: 26, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)' }}>
              {p.keyOutput}
            </div>
          </div>
        </div>

        {/* Decorative terminal-like output */}
        <div
          style={{
            padding: '16px 20px',
            background: '#0a0c10',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.06)',
            fontFamily: '"SF Mono", "Fira Code", monospace',
            fontSize: 24,
            opacity: outputOpacity,
          }}
        >
          <div style={{ color: '#10b981' }}>
            $ skill-check --status
          </div>
          <div style={{ color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            ✅ All {p.steps.length} steps completed
          </div>
          <div style={{ color: 'rgba(255,255,255,0.4)' }}>
            📊 Skill level: Intermediate → Advanced
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
